import React, { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import Button from '../common/Button';
import { Close, Send } from '@mui/icons-material';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background-color: ${props => getTheme(props.theme?.isDark).colors.surfaceElevated};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.lg};
  padding: ${props => getTheme(props.theme?.isDark).spacing.xl};
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${props => getTheme(props.theme?.isDark).shadows.xl};
  ${props => props.theme?.isDark && `
    box-shadow: 0 20px 25px rgba(0, 255, 0, 0.25);
    border-color: ${getTheme(true).colors.primary};
  `}
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  ${props => props.theme?.isDark && `
    text-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
  `}
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  cursor: pointer;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => getTheme(props.theme?.isDark).colors.hover};
    color: ${props => getTheme(props.theme?.isDark).colors.text};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
`;

const Input = styled.input`
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm} ${props => getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  font-family: ${props => getTheme(props.theme?.isDark).fonts.primary};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
    ${props => props.theme?.isDark && `
      box-shadow: 0 0 0 3px rgba(25, 184, 136, 0.1);
    `}
  }

  &::placeholder {
    color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  }
`;

const TextArea = styled.textarea`
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm} ${props => getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  font-family: ${props => getTheme(props.theme?.isDark).fonts.primary};
  resize: vertical;
  min-height: 120px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
    ${props => props.theme?.isDark && `
      box-shadow: 0 0 0 3px rgba(25, 184, 136, 0.1);
    `}
  }

  &::placeholder {
    color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  }
`;

const ErrorMessage = styled.div`
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  background-color: ${props => getTheme(props.theme?.isDark).colors.error}20;
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.error};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  color: ${props => getTheme(props.theme?.isDark).colors.error};
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  background-color: ${props => getTheme(props.theme?.isDark).colors.success}20;
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.success};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  color: ${props => getTheme(props.theme?.isDark).colors.success};
  font-size: 14px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  justify-content: flex-end;
  margin-top: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const PushNotificationModal = ({ isOpen, onClose, customerId, onSuccess }) => {
  const { isDark } = useTheme();
  const theme = { isDark };
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !body.trim()) {
      setError('Başlıq və məzmun tələb olunur');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { api } = await import('../../services/api');
      await api.sendDirectNotification(customerId, { title, body });
      setSuccess(true);
      setTitle('');
      setBody('');
      
      if (onSuccess) {
        onSuccess();
      }

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Xəta baş verdi');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setTitle('');
      setBody('');
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleClose}>
      <ModalContainer theme={theme} onClick={(e) => e.stopPropagation()}>
        <ModalHeader theme={theme}>
          <ModalTitle theme={theme}>Push Notification Göndər</ModalTitle>
          <CloseButton theme={theme} onClick={handleClose} disabled={loading}>
            <Close />
          </CloseButton>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          {error && (
            <ErrorMessage theme={theme}>
              {error}
            </ErrorMessage>
          )}

          {success && (
            <SuccessMessage theme={theme}>
              Notification uğurla göndərildi!
            </SuccessMessage>
          )}

          <FormGroup theme={theme}>
            <Label theme={theme}>Başlıq</Label>
            <Input
              theme={theme}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Notification başlığı..."
              disabled={loading}
              maxLength={100}
            />
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>Məzmun</Label>
            <TextArea
              theme={theme}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Notification məzmunu..."
              disabled={loading}
              maxLength={500}
            />
          </FormGroup>

          <ButtonGroup theme={theme}>
            <Button
              theme={theme}
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              <Close /> Ləğv et
            </Button>
            <Button
              theme={theme}
              type="submit"
              disabled={loading || !title.trim() || !body.trim()}
            >
              <Send /> Göndər
            </Button>
          </ButtonGroup>
        </Form>
      </ModalContainer>
    </Overlay>
  );
};

export default PushNotificationModal;


